import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { svg } from '../../assets/svg';
import logoNfc from '../../assets/bg/nfc-signal.png';
import logoPin from '../../assets/bg/location.png';
import bgFactory from '../../assets/bg/factory-img.jpeg';
import { hooks } from '../../hooks';
import { items } from '../../items';
import { actions } from '../../store/actions';
import { components } from '../../components';
import { Routes, TabScreens } from '../../routes';
import { axiosInstance } from '../../api/api';
import { usePatrolStatus } from '../../hooks/usePatrolStatus'
import { PatrolStatusDisplay } from '../../components/PatrolStatusDisplay'
import { usePermission } from '../../hooks/usePermission';

import type {
  LocationsType,
  HistoryType,
} from '../../types';

interface NDEFRecordInit {
  recordType: string;
  mediaType?: string;
  id?: string;
  data?: string | ArrayBuffer;
}

interface NDEFMessageInit {
  records: NDEFRecordInit[];
}

interface NDEFReader {
  write(message: NDEFMessageInit | string): Promise<void>;
  scan(): Promise<void>;
  onreading?: (event: any) => void;
  onreadingerror?: (event: any) => void;
}

declare var NDEFReader: {
  prototype: NDEFReader;
  new (): NDEFReader;
};

interface NFCRecord {
  type: string;
  value: string;
}

interface NFCMessage {
  serialNumber?: string;
  records: NFCRecord[];
  rawMessage?: any;
}

export const Home: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const navigate = hooks.useNavigate();
  const { locationsLoading, locations, isEmpty, lastUpdated } = hooks.useGetLocations();
  const { historyLoading, history } = hooks.useGetHistory();
  const [showGroupWarning, setShowGroupWarning] = useState(false);
  const { canIndex: canPatrolUserIndex } = usePermission('patrol_user_index');
  const { canIndex: canPatrolGroupIndex } = usePermission('patrol_group_index');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(locations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentLocations = locations.slice(startIndex, startIndex + itemsPerPage);

  const loading: boolean =
    locationsLoading ||
    historyLoading;

  const [opacity, setOpacity] = useState<number>(0);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);

  const { notificationsLoading, notifications } = hooks.useGetNotifications();
  const [readNotifications, setReadNotifications] = useState<Set<string>>(new Set());

  const [activeSlide, setActiveSlide] = useState(0);
  const handleSlideChange = (swiper: any) => setActiveSlide(swiper.activeIndex);

  const [nfcScanning, setNfcScanning] = useState<boolean>(false);
  const [nfcError, setNfcError] = useState<string | null>(null);
  const [nfcStatus, setNfcStatus] = useState<string | null>(null);
  const [lastScanned, setLastScanned] = useState<{
    code: string;
    id?: string | number | null;
    name?: string | null;
    serialNumber?: string | null;
  } | null>(null);

  const [supported] = useState<boolean>(() => typeof window !== 'undefined' && 'NDEFReader' in window);
  const ndefRef = useRef<NDEFReader | null>(null);
  
  const parseNFCMessage = (message: any) => {
    let spotData: any = null;
    let deepLinkUrl: string | null = null;

    for (const record of message.records) {
      try {
        if (record.recordType === 'text') {
          const textDecoder = new TextDecoder((record as any).encoding || 'utf-8');
          const text = textDecoder.decode(record.data!);
          try {
            const parsed = JSON.parse(text);
            if (!spotData) spotData = parsed;
          } catch {
            // not JSON, ignore
          }
        } else if (record.recordType === 'url') {
          const textDecoder = new TextDecoder();
          const url = textDecoder.decode(record.data!);
          if (url) deepLinkUrl = url;
        } else if (record.recordType === 'mime' && (record as any).mediaType === 'application/json') {
          const textDecoder = new TextDecoder();
          const jsonData = textDecoder.decode(record.data!);
          try {
            const parsed = JSON.parse(jsonData);
            if (!spotData) spotData = parsed;
          } catch {
            // ignore
          }
        } else {
          try {
            const textDecoder = new TextDecoder();
            const maybe = textDecoder.decode(record.data || new Uint8Array([]));
            try {
              const parsed = JSON.parse(maybe);
              if (!spotData) spotData = parsed;
            } catch {
              // ignore
            }
          } catch {
            // ignore unreadable record
          }
        }
      } catch {
        // ignore record-specific exceptions and continue parsing others
      }
    }

    const codeFromJson = spotData?.code || spotData?.id || spotData?.code_string || null;
    let codeFromUrl: string | null = null;
    if (deepLinkUrl) {
      try {
        const u = new URL(deepLinkUrl);
        const c = u.searchParams.get('code');
        if (c) codeFromUrl = c;
        else {
          const parts = u.pathname.split('/').filter(Boolean);
          if (parts.length) codeFromUrl = parts[parts.length - 1];
        }
      } catch {
        const q = deepLinkUrl.split('?')[1] || '';
        const params = new URLSearchParams(q);
        codeFromUrl = params.get('code') || null;
      }
    }

    return {
      spotData: spotData || null,
      code: (codeFromJson as string) || (codeFromUrl as string) || null,
    };
  };

  const startNFCScan = async () => {
    setNfcError(null);
    setNfcStatus(null);

    if (!supported) {
      setNfcError('Web NFC tidak mendukung di browser initAsyncCompiler, gunakan Chrome di Android. ');
      return;
    }

    try {
      const ndef = new NDEFReader();
      ndefRef.current = ndef;

      setNfcStatus('Meminta ijin untuk scan NFC...');
      await ndef.scan();
      setNfcScanning(true);
      setNfcStatus('Scanning — dekatkan HP mu ke area NFC tag');

      ndef.onreadingerror = () => {
        setNfcError('Tidak bisa membaca NFC tag, tolong coba lagi.');
        setNfcScanning(false);
      };

      ndef.onreading = (event: any) => {
        try {
          const { message, serialNumber } = event;
          setNfcStatus(`Tag read (SN: ${serialNumber ?? 'unknown'})`);

          const { spotData, code } = parseNFCMessage(message);

          if (!code) {
            setNfcError('Tag tidak memiliki data yang valid "code". Dibatalkan.');
            setNfcScanning(false);
            return;
          }
          const nfcPayload = {
            code: String(code),
            name: (spotData && (spotData.name || spotData.title)) || null,
            id: (spotData && (spotData.id ?? null)) || null,
            serialNumber: serialNumber ?? null,
            raw: spotData ?? null,
          };
          setLastScanned({
            code: nfcPayload.code,
            id: nfcPayload.id,
            name: nfcPayload.name,
            serialNumber: nfcPayload.serialNumber,
          });

          setNfcScanning(false);
          setNfcStatus('Tag parsed successfully.');

          dispatch(actions.setScreen(Routes.ClockInSuccess));

          navigate(Routes.ClockInSuccess, {
            state: {
              nfcData: {
                code: nfcPayload.code,
                name: nfcPayload.name,
                serialNumber: nfcPayload.serialNumber,
                id: nfcPayload.id || undefined,
              },
              spotName: nfcPayload.name,
              locationCode: nfcPayload.code,
            },
          });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          setNfcError(`Failed to parse NFC tag: ${msg}`);
          setNfcScanning(false);
        }
      };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setNfcError(msg || 'NFC scan failed (permission denied or not supported).');
      setNfcStatus(null);
      setNfcScanning(false);
    }
  };

  const stopNFCScan = () => {
    try {
      if (ndefRef.current) {
        ndefRef.current.onreading = undefined;
        ndefRef.current.onreadingerror = undefined;
      }
    } catch {
      // ignore
    }
    setNfcScanning(false);
    setNfcStatus('Scan stopped');
  };

  useEffect(() => {
    const patrolGroupId = localStorage.getItem('patrol_group_id');
    const hasPermission = canPatrolGroupIndex;
    
    if (!hasPermission && !patrolGroupId) {
      setShowGroupWarning(true);
    } else {
      setShowGroupWarning(false);
    }
  }, [canPatrolUserIndex]);

  useEffect(() => {
    return () => {
      try {
        if (ndefRef.current) {
          ndefRef.current.onreading = undefined;
          ndefRef.current.onreadingerror = undefined;
        }
      } catch {
        // ignore
      }
    };
  }, []);

  const handleClockInTap = () => {
    if (nfcScanning) stopNFCScan();
    else startNFCScan();
  };

  const renderGreeting = (): JSX.Element => {
    return (
      <div style={{marginBottom: 20, padding: '0 20px', textAlign: 'right'}}>
        <h2 style={{lineHeight: '1'}}>Halo,</h2>
        <p className='t14' style={{opacity: 0.7}}>
          Selamat datang di DPP Security Patrol!
        </p>
      </div>
    );  
  };
  
  const renderClockin = (): JSX.Element => {
  return (
    <section style={{padding: '40px 20px 40px', position: 'relative'}}>
      {/* Background Factory Image */}
      <div style={{
        backgroundImage: `url(${bgFactory})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 0,
        width: '100%',
        height: '100%',
      }}>
      </div>
      
      {/* Kotak Putih dengan Background Factory */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        padding: 20,
        borderRadius: 15,
        position: 'relative',
        zIndex: 1,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <div style={{padding: '20px', textAlign: 'right', paddingTop: 30,}}>
          <h1 style={{lineHeight: '1', fontWeight: 'bold', fontSize: '56px', textAlign:'center', color: '#66acb1'}}>
            <components.LiveClock />
          </h1>
          <h3 className='t14' style={{opacity: 0.7, textAlign:'center'}}>
            <components.LiveDate />
          </h3>
        </div>
        
        <div style={{marginBottom: 20, padding: '20px', display: 'flex', justifyContent: 'center'}}>
          <button
            onClick={handleClockInTap}
            style={{
              width: '240px',
              height: '240px',
              borderRadius: '50%',
              position: 'relative',
              backgroundColor: nfcScanning ? 'rgba(255, 152, 0, 0.15)' : 'rgba(0, 176, 185, 0.15)',
              border: '22px solid rgb(180 225 229)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              transition: 'background-color 0.3s ease',
              cursor: 'pointer'
            }}
          >
            {nfcScanning ? (
              <>
                <span className='t12' style={{
                  marginBottom: 15, 
                  color: '#FF9800', 
                  fontSize: '14px', 
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  Scanning NFC...
                </span>
                <div style={{
                  width: 70,
                  height: 70,
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #FF9800',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              </>
            ) : (
              <>
                <span className='t14' style={{
                  marginBottom: 15, 
                  fontSize: '13px',
                  textAlign: 'center',
                  color: '#2c3e50',
                  lineHeight: '1.3'
                }}>
                  Ketuk untuk memulai Patrol
                </span>
                <img
                  src={logoNfc}
                  alt="NFC Signal"
                  style={{
                    width: 90,
                    height: 90,
                    opacity: 0.8,
                  }}
                />
              </>
            )}
          </button>
        </div>

        {nfcScanning && (
          <div style={{
            marginBottom: 20,
            padding: '15px',
            backgroundColor: '#FFF3E0',
            borderRadius: '10px',
            border: '1px solid #FFB74D',
            textAlign: 'center'
          }}>
            <p className='t14' style={{
              color: '#E65100', 
              marginBottom: '10px',
              fontSize: '14px'
            }}>
              {nfcStatus || 'Ready to scan NFC tag'}
            </p>
            <button 
              onClick={stopNFCScan}
              style={{
                padding: '10px 20px',
                backgroundColor: '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Batalkan Scan
            </button>
          </div>
        )}

        {nfcError && (
          <div style={{
            marginBottom: 20,
            padding: '15px',
            backgroundColor: '#FFEBEE',
            borderRadius: '10px',
            border: '1px solid #EF5350',
            textAlign: 'center'
          }}>
            <p className='t14' style={{
              color: '#C62828',
              fontSize: '14px'
            }}>
              {nfcError}
            </p>
          </div>
        )}
        
        {/* {canPatrolUserIndex && (
          <div style={{ marginTop: 10 }}>
            <PatrolStatusDisplay />
          </div>
        )} */}
      </div>
    </section>
  );
};

const renderLocations = (): JSX.Element => {
  if (!canPatrolUserIndex) {
    return (
      <div style={{ marginBottom: 30 }}></div>
    );
  }

  return (
    <div style={{marginBottom: 40, marginTop: 40}}>
      <components.BlockHeading
        title='Lokasi'
        containerStyle={{marginLeft: 20, marginRight: 20, marginBottom: 14}}
      />

      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 16,
          padding: '8px 20px 12px 20px',
          marginBottom: '8px'
        }}>
          <button
            onClick={() => setCurrentPage(prev => prev - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '6px 12px',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              backgroundColor: currentPage === 1 ? '#f5f5f5' : 'white',
              color: currentPage === 1 ? '#bdbdbd' : '#333',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              minWidth: '60px'
            }}
          >
            ←
          </button>

          <span style={{ 
            fontSize: '13px', 
            color: '#666',
            minWidth: '60px',
            textAlign: 'center'
          }}>
            {currentPage} / {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 12px',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              backgroundColor: currentPage === totalPages ? '#f5f5f5' : 'white',
              color: currentPage === totalPages ? '#bdbdbd' : '#333',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              minWidth: '60px'
            }}
          >
            →
          </button>
        </div>
      )}

      {isEmpty ? (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          margin: '0 20px',
          border: '1px solid #e9ecef'
        }}>       
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#343a40',
            marginBottom: '8px'
          }}>
            Tidak ada patroli untuk saat ini
          </h3>
        </div>
      ) : (
        <ul>
          {currentLocations.map((location, index, array) => {
            const isLast = index === array.length - 1;
            return (
              <items.LocationsItem
                key={location.id}
                index={index}
                location={location}
                isLast={isLast}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

  const renderHistory = (): JSX.Element => (
    <div style={{ marginBottom: 10 }}>
      <div className="row-center-space-between">
        <components.BlockHeading title="Riwayat" containerStyle={{ marginLeft: 20, marginRight: 20, marginBottom: 14 }} />
        <span
          className="t14"
          style={{ marginRight: 20, marginLeft: 20, marginBottom: 14, cursor: 'pointer', color: 'var(--main-turquoise)', fontSize: 16 }}
          onClick={() => {
            dispatch(actions.setScreen(TabScreens.History));
            navigate(Routes.TabNavigator, { replace: true });
          }}
        >
          Lihat Semua &gt;
        </span>
      </div>

      {historyLoading ? (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <components.Loader />
        </div>
      ) : history.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
          <p className="t14">No patrol history yet</p>
          <p className="t12" style={{ opacity: 0.7 }}>
            Selesaikan patrol pertama mu untuk melihat riwayatmu disini
          </p>
        </div>
      ) : (
        <div 
          style={{ 
            padding: '0 20px 20px',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <ul 
            style={{ 
              display: 'inline-flex',
              gap: 15,
              minWidth: 'min-content'
            }}
          >
            {history.slice(0, 3).map((item: HistoryType, index: number) => (
              <li 
                key={item.id}
                style={{
                  display: 'inline-block',
                  width: 280,
                  flexShrink: 0
                }}
              >
                <items.HistoryItem 
                  history={item} 
                  index={index} 
                  isLast={index === history.length - 1}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const renderTestingAccess = (): JSX.Element | null => null;

  const renderContent = (): JSX.Element => {
    if (loading) return <components.Loader />;
    return (
      <main className="scrollable">
        {renderGreeting()}
        {renderClockin()}
        {renderLocations()}
        {renderHistory()}
      </main>
    );
  };

  return (
    <div id="screen" style={{ opacity }}>
      {renderContent()}
      {showGroupWarning && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '24px 28px',
              borderRadius: '12px',
              width: '80%',
              maxWidth: 350,
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ marginBottom: 10, color: '#c0392b', fontSize: 18 }}>⚠️ Anda Belum Tergabung</h3>
            <p className="t14" style={{ marginBottom: 20, color: '#555' }}>
              Anda belum dimasukkan ke grup patroli manapun.<br />
              Silakan hubungi <b>Security Admin</b> untuk mendapatkan akses.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => setShowGroupWarning(false)}
                style={{
                  backgroundColor: '#00b0b9',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Mengerti
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .scrollable::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};